import {
  Checkbox,
  ErrorList,
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import {
  CountryCode,
  getCountryCallingCode,
  isSupportedCountry,
} from 'libphonenumber-js'
import moment from 'moment-timezone'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { AnalyticsPageName } from 'src/analytics/analytics.models'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { resetSignupErrors } from 'src/app/Signup/redux/signup.actions'
import { SignupQueryParams } from 'src/app/Signup/signup.types'
import PhoneRequiredVerificationFlow from 'src/component-modal/PhoneRequiredVerificationFlow/PhoneRequiredVerificationFlow'
import AppName from 'src/components/AppName/AppName'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import CountryPhoneDropdown from 'src/components/CountryPhoneDropdown/CountryPhoneDropdown'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import PasswordInputField from 'src/components/PasswordInputField/PasswordInputField'
import PhoneTextInputField from 'src/components/PhoneTextInputField/PhoneTextInputField'
import SvgLocked from 'src/components/Svgs/SvgLocked'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { OrganizationRoles } from 'src/enums'
import { useBackgroundDispatch, useFormFields } from 'src/hooks'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { LibPhoneNumberCountryCodesSet } from 'src/models/country.models'
import { privacyUrl, signinUrl, signupUrl, termsUrl } from 'src/navigation/urls'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { selectIsExtraVerificationPopupOpenFromState } from 'src/redux/selectors/app.selectors'
import {
  selectNullSafeLocaleFromState,
  selectUserListModelFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import {
  selectAppName,
  selectBrandColorCta,
  selectCoBrandName,
  selectEnvironmentFromState,
} from 'src/redux/selectors/session.selectors'
import { PostUserRequest } from 'src/requests/postUser.request'
import { configureAmplify } from 'src/utils/cognito.utils'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { normalizeEmail, startsWithVowel } from 'src/utils/string.utils'
import {
  stringIsEmpty,
  stringIsNotURL,
} from 'src/utils/validation/string.validation'
import { css } from 'styled-components'
import countries, { Country } from 'world-countries'

export type SignupFormFlatFields = {
  email?: string
  firstName?: string
  lastName?: string
  phoneIsoCountry: string
  phone?: string
  password?: string
}

interface SignupFormProps {
  className?: string
  email?: string
  setEmail: (val: string) => void
  postUser: (body: Partial<PostUserRequest>) => void
  setShowHowToJoin: (val: boolean) => void
  queryParams?: SignupQueryParams
  isPostingUser: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
}

const SignupForm: React.FC<SignupFormProps> = (props) => {
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [hasSubmittedToMarketo, setHasSubmittedToMarketo] = useState(false)
  const locale = useSelector(selectNullSafeLocaleFromState)
  const brandCta = useSelector(selectBrandColorCta)
  const appName = useSelector(selectAppName)
  const { analytics } = useAnalyticsContext()
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const env = useSelector(selectEnvironmentFromState)
  const coBrandName = useSelector(selectCoBrandName)
  const user = useSelector(selectUserListModelFromState)
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: user?.organizationId,
  })
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled
  const isExtraVerificationPopupOpen = useSelector(
    selectIsExtraVerificationPopupOpenFromState,
  )

  const dispatch = useBackgroundDispatch()

  const {
    orgName,
    inviteeEmail,
    inviterName,
    inviterEmail,
    organizationRole,
    firstName: inviteeFirstName,
    lastName: inviteeLastName,
    phone: inviteePhone,
  } = props?.queryParams || {}

  const signupAndJoin = !!(
    orgName &&
    inviteeEmail &&
    inviterName &&
    inviterEmail
  )
  const showGuestCopy =
    organizationRole === OrganizationRoles.guest && showGuestRole

  const [confirmTermsAndPrivacy, setConfirmTermsAndPrivacy] = useState(false)
  const [confirmTermsAndPrivacyError, setConfirmTermsAndPrivacyError] =
    useState(false)

  useEffect(() => {
    if (inviteeEmail) {
      const normalizedEmail = normalizeEmail(inviteeEmail)
      props.setEmail(normalizedEmail)
      onFlatFieldChange('email', normalizedEmail)
    }
  }, [inviteeEmail])

  // Configure Amplify on mount using unified configureAmplify utility
  const [didConfigAmplify, setDidConfigAmplify] = useState<boolean>(false)
  useEffect(() => {
    if (didConfigAmplify === false && env) {
      configureAmplify(env)
      setDidConfigAmplify(true)
    }
  }, [env, didConfigAmplify])

  useEffect(() => {
    // Marketo (script)
    const script = document.createElement('script')
    script.nonce = window.__NONCE__
    script.src = '//964-YBI-786.mktoweb.com/js/forms2/js/forms2.min.js'

    if (!document.getElementById('marketo')) {
      script.onload = loadMarketoForm
    }

    // Conversation Path (config)
    // must be *before* the ConversionPath (script)
    const conversionPathConfig = document.createElement('script')
    conversionPathConfig.type = 'application/json'
    conversionPathConfig.nonce = window.__NONCE__
    conversionPathConfig.id = 'CP_TouchHistoryOptions'
    conversionPathConfig.text = `
      {
        "session": {
          "internalDomains": {
            "alternates": ["paywithextend.com"]
          }
        },
        "nameMappings": {
          "RC_First_Source__c": "Original_Source__c",
          "RC_First_Source_Detail__c": "Original_Source_Info__c",
          "RC_First_Medium__c": "Original_Medium__c",
          "RC_First_Campaign__c": "Original_Campaign__c",
          "RC_First_Content__c": "Original_Content__c",
          "RC_First_Term__c": "Original_Term__c",
          "GCLID__c": "GCLID__c"
        },
        "stageMappings": {
          "initial": [
            {
              "when":    "{current.query.gclid}",
              "matches": "(.+)",
              "set":     "GCLID__c",
              "to":      "$1"
            }
          ]
        }
      }
    `

    document.head.appendChild(script)
    document.head.appendChild(conversionPathConfig)
  }, [])

  const loadMarketoForm = (): void => {
    const script = document.createElement('script')
    script.id = 'marketo'
    script.nonce = window.__NONCE__
    script.text = `
      MktoForms2.loadForm('//964-YBI-786.mktoweb.com', '964-YBI-786', 1004);
      MktoForms2.whenReady(function(form){
        form.onSuccess(function(vals,thankYouURL){
          return false;
        });
      });

      // Conversion Path (script)
      const conversionPathScript = document.createElement('script')
      conversionPathScript.nonce = window.__NONCE__
      conversionPathScript.src = '//du4pg90j806ok.cloudfront.net/js/touch-history/dist/conversionpath-0.4.3.min.js'
      document.head.appendChild(conversionPathScript)
    `
    document.head.appendChild(script)
  }

  // Submit to Marketo only AFTER the Extend API call succeeds
  // isExtraVerificationPopupOpen becomes true only on POST_USER_SUCCESS
  useEffect(() => {
    if (isSubmitted && isExtraVerificationPopupOpen && !hasSubmittedToMarketo) {
      setHasSubmittedToMarketo(true)
      const mktoScript = document.createElement('script')
      mktoScript.nonce = window.__NONCE__
      mktoScript.text = `
        var marketoForm = MktoForms2.allForms()[0];
        let obj = {
          "Email": '${props.email || undefined}',
          "FirstName": '${flatFields.firstName}',
          "LastName": '${flatFields.lastName}',
          "Phone": '${flatFields.phone}'
        };
        marketoForm?.addHiddenFields(obj);
        marketoForm.submit();
      `
      document.body.appendChild(mktoScript)
      return (): void => {
        document.body.removeChild(mktoScript)
      }
    }
  }, [isSubmitted, isExtraVerificationPopupOpen, hasSubmittedToMarketo])

  const isoToCountryMap: Record<string, Country> = useMemo(
    () =>
      countries.reduce(
        (acc, curr) => ({
          ...acc,
          [curr.cca2]: curr,
        }),
        {},
      ),
    [countries],
  )

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()

    dispatch(resetSignupErrors())

    if (!confirmTermsAndPrivacy && signupAndJoin) {
      setConfirmTermsAndPrivacyError(true)
    }

    if (signupAndJoin) {
      analytics?.track('bespoke org user signup - sign up', {
        screen: signupUrl(),
        'T&C_checkbox': confirmTermsAndPrivacy,
      })
    } else {
      analytics?.track('successful signup', {
        screen: signupUrl(),
      })
    }

    const getFormattedPhone = ({
      phoneIsoCountry,
      phone,
    }: {
      phoneIsoCountry: string
      phone: string | undefined
    }): string | null => {
      if (isoToCountryMap[phoneIsoCountry] === undefined) {
        return null
      }
      if (LibPhoneNumberCountryCodesSet.has(phoneIsoCountry)) {
        if (phone && phone.length !== 0) {
          return `+${getCountryCallingCode(phoneIsoCountry as CountryCode)}${phone}`
        }
      }
      return !phone ? null : phone || null
    }

    const passedFlatFieldsValidation = flatFieldsSetErrors()
    if (
      passedFlatFieldsValidation &&
      (confirmTermsAndPrivacy || !signupAndJoin)
    ) {
      setIsSubmitted(true)
      const normalizedEmail = normalizeEmail(flatFields.email!)
      props.postUser({
        email: normalizedEmail,
        password: flatFields.password,
        firstName: flatFields.firstName,
        lastName: flatFields.lastName,
        phone: getFormattedPhone({
          phoneIsoCountry: flatFields.phoneIsoCountry,
          phone: flatFields.phone,
        }),
        timezone: moment.tz.guess(),
        locale: locale,
      })
    }
  }

  const translate = useTranslate()
  const initialSignupFields: SignupFormFlatFields = {
    email: props.email,
    // Existing invite flow stores inviteeEmail through props.email because that
    // field is locked and shared with Marketo/error handling. Bulk-invite
    // profile prefills are editable, so they stay local form initial values.
    firstName: inviteeFirstName,
    lastName: inviteeLastName,
    phone: inviteePhone,
    phoneIsoCountry: isSupportedCountry(locale.slice(-2).toUpperCase())
      ? (locale.slice(-2).toUpperCase() as CountryCode)
      : 'US',
  }

  const {
    fields: flatFields,
    fieldsErrors: flatFieldsErrors,
    onFieldChange: onFlatFieldChange,
    setErrorsAndReturnDidPassValidation: flatFieldsSetErrors,
  } = useFormFields<SignupFormFlatFields>(
    initialSignupFields,
    () => ({
      email: [stringIsEmpty()],
      firstName: [stringIsEmpty(), stringIsNotURL()],
      lastName: [stringIsEmpty(), stringIsNotURL()],
      phoneIsoCountry: [stringIsEmpty()],
      phone: [stringIsEmpty()],
      password: [stringIsEmpty()],
    }),
    props.errorDetailMap,
  )

  const howToJoinSegmentEvent = (): void => {
    analytics?.track("Sign up - Here's how to join them", {
      intent: "Here's how to join them",
      interaction: 'text link press',
      screen: signupUrl(),
    })
  }

  useEffect((): void => {
    if (signupAndJoin) {
      analytics?.page(AnalyticsPageName.BespokeUserSignUp, {
        screen: calculatedBackgroundLocation.pathname,
      })
    }
  }, [])

  return (
    <div className={props.className}>
      <form
        id="mktoForm_1004"
        css={css`
          display: none;
        `}
      ></form>
      <form
        css={css`
          display: flex;
          flex-direction: column;
        `}
        onSubmit={onSubmit}
      >
        <span
          css={css`
            ${getFontCss(
              32,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
            margin-bottom: 30px;
          `}
        >
          {signupAndJoin
            ? translate('Sign up for ${appName} and join ${orgName}', {
                appName: <AppName />,
                orgName: (
                  <span
                    css={css`
                      ${getFontCss(
                        32,
                        themeConstants.colors.highlight,
                        styleConstants.fontFamilies.medium,
                      )};
                    `}
                  >
                    {orgName}
                  </span>
                ),
              })
            : translate('Sign up your company with ${appName}!', {
                appName: <AppName />,
              })}
        </span>
        <p
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
            margin-bottom: 40px;
          `}
        >
          {signupAndJoin
            ? translate(
                showGuestCopy
                  ? '${inviterNameAndEmail} has added you to their organization as a Guest. If you already have an account, you can view this organization after you ${logInLink}.'
                  : '${inviterNameAndEmail} has invited you to join their organization in ${appName}. If you already have an account, you can accept or decline after you ${logInLink}.',
                {
                  inviterNameAndEmail: (
                    <span
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )};
                      `}
                    >
                      {`${inviterName} (`}
                      {
                        <HoverableLink
                          size={HoverableLinkSizes.Large}
                          href={`mailto:${inviterEmail}`}
                          underline
                          ariaLabelSource={inviterEmail}
                        >
                          {inviterEmail}
                        </HoverableLink>
                      }
                      {`)`}
                    </span>
                  ),
                  appName: <AppName />,
                  logInLink: (
                    <HoverableLink
                      size={HoverableLinkSizes.Large}
                      to={signinUrl()}
                      underline
                      ariaLabelSource={'log in'}
                    >
                      {translate('log in')}
                    </HoverableLink>
                  ),
                },
              )
            : startsWithVowel(appName)
              ? translate(
                  'Does your company already have an ${appName} account? ${joinLink}',
                  {
                    appName: <AppName />,
                    joinLink: (
                      <HoverableLink
                        size={HoverableLinkSizes.Large}
                        onClick={(): void => {
                          props.setShowHowToJoin(true)
                          howToJoinSegmentEvent()
                        }}
                        underline
                        ariaLabelSource={"Here's how to join them!"}
                      >
                        {translate("Here's how to join them!")}
                      </HoverableLink>
                    ),
                  },
                )
              : translate(
                  'Does your company already have a ${appName} account? ${joinLink}',
                  {
                    appName: <AppName />,
                    joinLink: (
                      <HoverableLink
                        size={HoverableLinkSizes.Large}
                        onClick={(): void => {
                          props.setShowHowToJoin(true)
                          howToJoinSegmentEvent()
                        }}
                        underline
                        ariaLabelSource={"Here's how to join them!"}
                      >
                        {translate("Here's how to join them!")}
                      </HoverableLink>
                    ),
                  },
                )}
        </p>
        <TextInputField
          id={'email'}
          design={TextInputFieldDesigns.New}
          fieldLabel={translate('Email')}
          value={flatFields['email']}
          placeholder={translateSingleString(
            locale,
            'Enter a complete email address',
          )}
          onChange={(val: string): void => {
            props.setEmail(val)
            onFlatFieldChange('email', val)
          }}
          type={'email'}
          autoComplete={'email'}
          disabled={signupAndJoin}
          rightImage={
            <>
              {signupAndJoin && (
                <div
                  css={css`
                    opacity: 50%;
                    margin-top: 2px;
                    padding-right: 10px;
                  `}
                >
                  <SvgLocked
                    css={css`
                      transform: scale(calc(18 / 24));
                      transform-origin: center left;
                      flex-shrink: 0;
                    `}
                    fill={brandCta}
                  />
                </div>
              )}
            </>
          }
          errors={flatFieldsErrors['email'] || props.errorDetailMap?.['email']}
        />
        <input
          style={{ display: 'none' }}
          value={flatFields.password}
          placeholder={'Password'}
          type={'password'}
          autoComplete={'new-password'}
        />
        <TextInputField
          id={'firstName'}
          css={css`
            &:nth-child(n + 2) {
              margin-top: 20px;
            }
          `}
          fieldLabel={translate('First Name')}
          design={TextInputFieldDesigns.New}
          errors={flatFieldsErrors['firstName']}
          placeholder={translateSingleString(
            locale,
            'Enter your legal first name',
          )}
          value={flatFields['firstName']}
          onChange={(val: string): void => onFlatFieldChange('firstName', val)}
        />
        <TextInputField
          id={'lastName'}
          css={css`
            &:nth-child(n + 2) {
              margin-top: 20px;
            }
          `}
          fieldLabel={translate('Last Name')}
          design={TextInputFieldDesigns.New}
          errors={flatFieldsErrors['lastName']}
          placeholder={translateSingleString(
            locale,
            'Enter your legal last name',
          )}
          value={flatFields['lastName']}
          onChange={(val: string): void => onFlatFieldChange('lastName', val)}
        />
        <div
          css={css`
            margin-top: 20px;
            display: flex;
            flex-direction: column;
          `}
        >
          <FieldLabel>{translate('Phone Number')}</FieldLabel>
          <div
            css={css`
              display: flex;
              gap: 10px;
              align-items: flex-start;
            `}
          >
            <CountryPhoneDropdown
              id={'signUp'}
              selectedValue={flatFields.phoneIsoCountry}
              chooseOption={(val: string): void =>
                onFlatFieldChange('phoneIsoCountry', val)
              }
            />
            <PhoneTextInputField
              id={'signUp'}
              css={css`
                flex-grow: 2;
              `}
              errors={flatFieldsErrors.phone}
              value={flatFields.phone}
              countryIso={flatFields.phoneIsoCountry}
              onChange={(val: string): void => onFlatFieldChange('phone', val)}
            />
          </div>
          <span
            css={css`
              margin-top: 8px;
              padding: 0px 20px;
              ${getFontCss(
                12,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            {translate(
              'By verifying your phone number, you agree to receive text messages from ${appName} for verification passcodes and account-related alerts. Message and data rates may apply.',
              {
                appName: <AppName />,
              },
            )}
          </span>
        </div>
        <PasswordInputField
          id={'signUp'}
          css={css`
            margin-top: 30px;
          `}
          errors={flatFieldsErrors.password}
          value={flatFields.password}
          onChange={(val: string): void => onFlatFieldChange('password', val)}
          isNewPassword
        />
        {props.topLevelError && (
          <div
            css={css`
              margin-top: 20px;
              align-self: center;
            `}
          >
            <ErrorList
              errors={[
                props.topLevelError === 'GENERIC_ERROR'
                  ? translate(
                      'There was an error registering your account. Please try again',
                    )
                  : props.topLevelError === 'COMPROMISED_PASSWORD'
                    ? translate(
                        'The password you entered is known to be on a list of globally compromised passwords. Please try a different password.',
                      )
                    : props.topLevelError || '',
              ]}
            />
          </div>
        )}
        <div
          css={css`
            display: flex;
            align-self: center;
            margin-top: 40px;
            ${getFontCss(
              12,
              styleConstants.colors.gray_darkest,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          {signupAndJoin ? (
            <div
              css={css`
                display: flex;
              `}
            >
              <Checkbox
                onClick={(): void => {
                  setConfirmTermsAndPrivacy(!confirmTermsAndPrivacy)
                  setConfirmTermsAndPrivacyError(false)
                }}
                isSelected={confirmTermsAndPrivacy}
                css={css`
                  align-items: start;
                `}
                isRequired={confirmTermsAndPrivacyError}
                testId={'confirmTermsAndPrivacy'}
              />
              <span>
                {translate(
                  'By checking this box, you agree to our ${termsAndConditions} and ${privacyPolicy}. Your information is secure. We neither sell nor publicize your information outside of ${appName}.',
                  {
                    termsAndConditions: (
                      <HoverableLink
                        size={HoverableLinkSizes.Small}
                        href={termsUrl()}
                        underline
                        regular
                        ariaLabelSource={'Terms and Conditions'}
                        css={css`
                          color: ${styleConstants.colors.gray_darkest};
                          &:hover {
                            color: ${styleConstants.colors.black};
                          }
                        `}
                      >
                        {translate('Terms and Conditions')}
                      </HoverableLink>
                    ),
                    privacyPolicy: (
                      <HoverableLink
                        size={HoverableLinkSizes.Small}
                        href={privacyUrl()}
                        underline
                        regular
                        ariaLabelSource={'Privacy Policy'}
                        css={css`
                          color: ${styleConstants.colors.gray_darkest};
                          &:hover {
                            color: ${styleConstants.colors.black};
                          }
                        `}
                      >
                        {translate('Privacy Policy')}
                      </HoverableLink>
                    ),
                    appName: <AppName />,
                  },
                )}
              </span>
            </div>
          ) : (
            <>
              <SvgLocked
                css={css`
                  margin-top: 2px;
                  transform: scale(calc(12 / 24));
                  transform-origin: top left;
                  flex-shrink: 0;
                `}
                fill={styleConstants.colors.gray_darkest}
              />
              <span>
                {translate(
                  'By signing up, you agree to our ${termsAndConditions} and ${privacy}. Your information is secure. We neither sell nor publicize your information outside of ${appName}.',
                  {
                    termsAndConditions: (
                      <HoverableLink
                        size={HoverableLinkSizes.Small}
                        href={termsUrl()}
                        underline
                        regular
                        ariaLabelSource={'Terms and Conditions'}
                        css={css`
                          color: ${styleConstants.colors.gray_darkest};
                          &:hover {
                            color: ${styleConstants.colors.black};
                          }
                        `}
                      >
                        {translate('Terms and Conditions')}
                      </HoverableLink>
                    ),
                    privacy: (
                      <HoverableLink
                        size={HoverableLinkSizes.Small}
                        href={privacyUrl()}
                        underline
                        regular
                        ariaLabelSource={'Privacy Policy'}
                        css={css`
                          color: ${styleConstants.colors.gray_darkest};
                          &:hover {
                            color: ${styleConstants.colors.black};
                          }
                        `}
                      >
                        {translate('Privacy Policy')}
                      </HoverableLink>
                    ),
                    appName: <AppName />,
                  },
                )}
              </span>
            </>
          )}
        </div>
        {confirmTermsAndPrivacyError && (
          <div
            css={css`
              margin-top: 10px;
            `}
          >
            <ErrorList
              errors={[
                confirmTermsAndPrivacyError
                  ? translate('Please confirm the above before continuing')
                  : '',
              ]}
            />
          </div>
        )}
        {coBrandName !== undefined && (
          <span
            css={css`
              align-self: center;
              margin-top: 20px;
              ${getFontCss(
                12,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            {translate(
              'Our Terms and Conditions do not change the agreement that you have with your financial institution regarding your card program.',
            )}
          </span>
        )}
        <Button
          id={'signupBtn'}
          data-testid="sign-up-button"
          type={ButtonTypes.submit}
          css={css`
            margin-top: 40px;
          `}
          design={ButtonDesigns.designCta}
          text={translate('Sign Up')}
          loading={props.isPostingUser}
          disabled={props.isPostingUser}
          big
        />
        {!signupAndJoin && (
          <span
            css={css`
              align-self: center;
              margin-top: 20px;
              ${getFontCss(
                12,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            {translate('Already with ${appName}? ${logIn}', {
              logIn: (
                <HoverableLink
                  size={HoverableLinkSizes.Small}
                  to={signinUrl()}
                  underline
                  ariaLabelSource={'Log in'}
                >
                  {translate('Log in')}
                </HoverableLink>
              ),
              appName: <AppName />,
            })}
          </span>
        )}
      </form>
      {isExtraVerificationPopupOpen && flatFields.password && isSubmitted && (
        <PhoneRequiredVerificationFlow
          password={flatFields.password}
          phoneIsoCountry={flatFields.phoneIsoCountry}
          phone={flatFields.phone as string}
        />
      )}
    </div>
  )
}

export default SignupForm
